import { Box, Text } from "@chakra-ui/react";
import { type ReactNode } from "react";

import { Info } from "ui/alert";
import { Link } from "ui/link";
import { Strong } from "ui/strong";

export const WarehouseExport = ({
  source = "your data warehouse",
  storage = "cloud storage",
  storageRequirement,
  children,
}: {
  source?: string;
  storage?: string;
  /** One sentence naming the storage this source needs. */
  storageRequirement?: ReactNode;
  children?: ReactNode;
}) => (
  <Info>
    <Box display="flex" flexDirection="column" gap={6}>
      <Text textStyle="paragraph" my={0}>
        Hightouch can use <Strong>Warehouse Export</Strong> with {source}.
        Instead of streaming every row through Hightouch, it has {source} write
        query results directly into {storage}, which Hightouch then reads. This
        speeds up syncs of large models. Warehouse Export runs automatically when
        your{" "}
        <Link variant="inline" href="/syncs/lightning-sync-engine">
          sync engine
        </Link>{" "}
        and{" "}
        <Link variant="inline" href="/security/storage">
          storage
        </Link>{" "}
        support it, and Hightouch falls back to standard export when they don't,
        so your syncs keep running either way.
      </Text>
      {storageRequirement && (
        <Text textStyle="paragraph" my={0}>
          {storageRequirement}
        </Text>
      )}
      <Text textStyle="paragraph" my={0}>
        If Warehouse Export runs into a fixable configuration problem, such as a
        storage-integration or bucket-permission issue, Hightouch stops using it
        for the affected syncs so it isn't retried on every run, and shows this
        on the sync and on this source's configuration page. After you resolve
        the problem, re-enable Warehouse Export from an individual sync, or for
        all of this source's syncs at once from the source configuration page,
        and Hightouch uses it again on the next run.
      </Text>
      <Text textStyle="paragraph" my={0}>
        A one-off problem on a single run, such as a row that exceeds the
        Warehouse Export size limit, isn't treated as a configuration issue: that
        run falls back to standard export and says so on the run summary, and
        Hightouch automatically retries Warehouse Export on the next run.
      </Text>
      {children}
    </Box>
  </Info>
);
